"use client";

import React, { useActionState, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { formOptions, initialFormState, mergeForm, useForm, useStore, useTransform } from "@tanstack/react-form-nextjs";
import { useLocale } from "next-intl";
import type { FormWizardDataFromQuery, FormWizardStepFromQuery, FormFieldFromQuery } from "@/lib/types";
import { getI18nString, getI18nValue } from "@/lib/utils";
import { commonValidation, generateDefaultFormWizardValues, type FormFieldValue } from "@/lib/form-helpers";
import { pushEvent } from "@/lib/analytics";
import { HoneypotField } from "@/components/form/honeypot-field.component";
import { HONEYPOT_FIELD } from "@/lib/honeypot";
import { useStalledSubmission } from "@/lib/hooks/use-stalled-submission";
import { ErrorMessage } from "@repo/ui/FormComponents/ErrorMessage";
import { FormFieldGroup } from "@/components/form/form-field-group.component";
import { FormFieldRenderer } from "@/components/form/form-field-renderer.component";
import { RichTextRight } from "@/components/common/rich-text/rich-text-right.component";
import { WizardStepIndicator } from "./wizard-step-indicator.component";
import { WizardStepHeader } from "./wizard-step-header.component";
import { WizardNavigation } from "./wizard-navigation.component";
import { WizardSuccess } from "./wizard-success.component";

/**
 * Server action signature for wizard submission. Kept generic so the component
 * doesn't import the real action directly — the block wrapper injects it. This
 * keeps `FormWizard` presentational and lets Storybook stub it without pulling
 * MongoDB + Sanity client into the browser bundle.
 */
export type WizardSubmitAction = (prev: unknown, formData: FormData) => Promise<unknown>;
export interface FormWizardProps {
  wizardData: FormWizardDataFromQuery;
  submitAction: WizardSubmitAction;
}
export const FormWizard = ({
  wizardData,
  submitAction
}: FormWizardProps) => {
  const locale = useLocale();
  const [state, action] = useActionState(submitAction, initialFormState);
  const [isPending, startTransition] = useTransition();
  const hasStalled = useStalledSubmission(isPending);
  const [currentStep, setCurrentStep] = useState(0);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const hasMountedRef = useRef(false);

  // Scroll the wizard back to the top on each step change so the user doesn't
  // land mid-page (e.g. down at the footer) after advancing. Skips the initial
  // mount so the page isn't yanked on first render.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    formRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }, [currentStep]);
  const steps = useMemo<FormWizardStepFromQuery[]>(() => wizardData.steps ?? [], [wizardData.steps]);
  const formOpts = formOptions({
    defaultValues: generateDefaultFormWizardValues(wizardData)
  });
  const form = useForm({
    ...formOpts,
    transform: useTransform(baseForm => mergeForm(baseForm, state ?? {}), [state]),
    onSubmit: async ({
      value
    }) => {
      const formDataToSubmit = new FormData();
      if (wizardData._id) {
        formDataToSubmit.append("formWizardId", wizardData._id);
      }
      const honeypotValue = honeypotRef.current?.value.trim();
      if (honeypotValue) {
        formDataToSubmit.append(HONEYPOT_FIELD, honeypotValue);
      }
      Object.entries(value).forEach(([key, val]) => {
        if (val !== null && val !== undefined) {
          if (Array.isArray(val)) {
            // Arrays of objects (e.g. a repeater's rows) are JSON-encoded into a
            // single entry; primitive arrays append one entry per item.
            const hasObjects = val.some(item => item !== null && typeof item === "object");
            if (hasObjects) {
              formDataToSubmit.append(key, JSON.stringify(val));
            } else {
              val.forEach(item_0 => formDataToSubmit.append(key, String(item_0)));
            }
          } else {
            formDataToSubmit.append(key, String(val));
          }
        }
      });
      startTransition(() => {
        action(formDataToSubmit);
      });
    }
  });
  const formErrors = useStore(form.store, formState => formState.errors);
  const isSubmissionSuccessful = !state && form.state.isSubmitted;
  const hasTrackedSubmit = useRef(false);
  useEffect(() => {
    if (isSubmissionSuccessful && !hasTrackedSubmit.current) {
      hasTrackedSubmit.current = true;
      pushEvent({
        event: "form_submit",
        form_id: wizardData._id,
        form_name: wizardData.name ?? undefined,
        form_type: "enquiry"
      });
    }
  }, [isSubmissionSuccessful, wizardData._id, wizardData.name]);
  const totalSteps = steps.length;
  const isFirstStep = currentStep === 0;
  const isFinalStep = currentStep === totalSteps - 1;
  const step = steps[currentStep];
  const renderField = (field: FormFieldFromQuery) => {
    if (!field.fieldName) return <p key="no-name">Field has no name</p>;
    return <form.Field key={field.fieldName} name={field.fieldName} validators={commonValidation(field)}>
        {fieldProps => <FormFieldRenderer field={field} formWizardId={wizardData._id} fieldApi={{
        name: fieldProps.name,
        value: fieldProps.state.value,
        handleChange: (value_0: unknown) => fieldProps.handleChange(() => value_0 as FormFieldValue),
        handleBlur: fieldProps.handleBlur,
        state: {
          meta: {
            errors: fieldProps.state.meta.errors.filter(Boolean) as string[]
          }
        }
      }} />}
      </form.Field>;
  };
  const validateCurrentStep = async (): Promise<boolean> => {
    const stepFields = (step?.fieldGroups ?? []).flatMap(item_1 => item_1._type === "formTextBlock" ? [] : (item_1 as {
      fields?: FormFieldFromQuery[];
    }).fields ?? []).filter((field_0): field_0 is FormFieldFromQuery => Boolean(field_0?.fieldName));
    const results = await Promise.all(stepFields.map(field_1 => form.validateField(field_1.fieldName as string, "change")));
    return results.every(errors => !errors || Array.isArray(errors) && errors.length === 0);
  };
  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      const firstInvalid = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (!firstInvalid) return;
      firstInvalid.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      firstInvalid.focus({
        preventScroll: true
      });
    });
  };
  const handleNext = async () => {
    if (isAdvancing) return;
    setIsAdvancing(true);
    try {
      const isValid = await validateCurrentStep();
      if (isValid && !isFinalStep) {
        setCurrentStep(prev => prev + 1);
      } else if (!isValid) {
        scrollToFirstError();
      }
    } finally {
      setIsAdvancing(false);
    }
  };
  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };
  if (totalSteps === 0) {
    return null;
  }
  if (isSubmissionSuccessful) {
    return <WizardSuccess heading={wizardData.successHeading} message={wizardData.successMessage} image={wizardData.successImage} buttonLabel={wizardData.successButtonLabel} onStartOver={() => {
      form.reset();
      setCurrentStep(0);
    }} action={wizardData.successAction} hideButton={wizardData.hideSuccessButton ?? false} />;
  }
  if (!step) {
    return null;
  }
  const stepLabel = getI18nString(step.label, `Step ${currentStep + 1}`, locale);
  const stepDescription = getI18nValue(step.description, locale);
  return <form ref={formRef} noValidate onSubmit={e => {
    e.preventDefault();
    form.handleSubmit(e);
  }} data-testid="form-wizard" data-current-step={currentStep + 1} className="col-span-full grid scroll-mt-[140px] desktop:scroll-mt-[184px] grid-cols-subgrid gap-y-16 tablet:gap-y-12">
      {wizardData._id && <input type="hidden" name="formWizardId" value={wizardData._id} />}

      <HoneypotField ref={honeypotRef} />

      <div className="col-span-full flex flex-col gap-6 tablet:col-span-3 desktop:col-span-4">
        <WizardStepIndicator steps={steps} currentStep={currentStep} locale={locale} />
        <WizardStepHeader label={stepLabel} description={stepDescription} />
      </div>

      <div className="col-span-full flex flex-col gap-8 tablet:col-span-5 tablet:col-start-4 desktop:col-span-6 desktop:col-start-7">
        <div className="flex flex-col gap-12">
          {step.fieldGroups?.map((item_2, itemIndex) => {
          if (item_2._type === "formTextBlock") {
            const content = (item_2 as {
              content?: unknown;
            }).content;
            if (!Array.isArray(content) || content.length === 0) return null;
            return <RichTextRight key={`${currentStep}-${itemIndex}`} richText={content} as="form" className="text-text-primary" />;
          }
          const group = item_2 as Extract<typeof item_2, {
            fields?: unknown;
          }>;
          return <FormFieldGroup key={`${currentStep}-${itemIndex}`} headline={group.headline} description={group.description} numberOfColumns={group.numberOfColumns}>
                {group.fields?.map(field_2 => renderField(field_2))}
              </FormFieldGroup>;
        })}
        </div>

        {formErrors.map((error, index) => <ErrorMessage key={index} error={error} />)}

        {hasStalled && <ErrorMessage error="We couldn't reach the server. Please check your connection and try again." />}

        <form.Subscribe selector={formState_0 => ({
        values: formState_0.values,
        isSubmitting: formState_0.isSubmitting
      })}>
          {({
          values,
          isSubmitting
        }) => {
          const stepInputFields = (step.fieldGroups ?? []).flatMap(item_3 => item_3._type === "formTextBlock" ? [] : (item_3 as {
            fields?: FormFieldFromQuery[];
          }).fields ?? []);
          // Text-only step (no inputs to fill) — let the user proceed.
          const canAdvance = stepInputFields.length === 0 || stepInputFields.some(field_3 => {
            if (!field_3.fieldName) return false;
            const value_1 = (values as Record<string, unknown>)[field_3.fieldName];
            if (Array.isArray(value_1)) return value_1.length > 0;
            if (typeof value_1 === "boolean") return value_1;
            if (typeof value_1 === "number") return true;
            return value_1 != null && String(value_1).trim() !== "";
          });
          return <WizardNavigation isFirstStep={isFirstStep} isFinalStep={isFinalStep} isPending={isPending && !hasStalled} isAdvancing={isAdvancing} isSubmitting={isSubmitting} canAdvance={canAdvance} submitLabel={wizardData.submitLabel || "Submit"} onBack={handleBack} onNext={handleNext} />;
        }}
        </form.Subscribe>
      </div>
    </form>;
};